import { useRecoilValue, useSetRecoilState } from "recoil";
import { useTheme } from "../../hooks/useTheme";
import { useStrings } from "../../hooks/useStrings";
import { helpFilmPlayingState, helpFilmsWatchedState } from "../../states/helpFilmState";
import { chapterTitle, clock, FilmId, FilmPart, filmLanguage, filmThumbnailAt, helpFilm, partSeconds, WHOLE } from "./helpFilms";

/** A help film's part as this person sees it: in their language, its length, and whether they have watched it. */
export function useHelpFilm(id: FilmId, part: FilmPart = WHOLE) {
    const { strings } = useStrings();
    const film = helpFilm(id);
    const language = filmLanguage(film, strings.getLanguage());
    const { start, end } = partSeconds(film, language, part);
    const watchedParts = useRecoilValue(helpFilmsWatchedState);
    const setPlaying = useSetRecoilState(helpFilmPlayingState);
    return {
        film,
        language,
        start,
        /** the part's picture: the film's poster for a whole film (its first seconds are the title card), else just after the chapter starts */
        frame: start === 0 ? (film.cuts[language]?.poster ?? 2) : start + 2,
        length: end - start,
        watched: watchedParts[`${id}:${part.from}`] !== undefined,
        watchedAny: Object.keys(watchedParts).some((key) => key.startsWith(id + ":")),
        play: (place: string) => setPlaying({ film: id, part, place }),
    };
}

export function filmTitle(strings: ReturnType<typeof useStrings>["strings"], id: FilmId): string {
    switch (id) {
        case "setup": return strings.Setup.SetupVideo;
        case "roles": return strings.HelpFilms.Roles;
        case "jobs": return strings.HelpFilms.Jobs;
        case "settings": return strings.HelpFilms.Settings;
        case "gps": return strings.HelpFilms.Gps;
        case "invites": return strings.HelpFilms.Invites;
        case "integrations": return strings.HelpFilms.Integrations;
    }
}

function PlayMark() {
    return <svg width="8" height="9" viewBox="0 0 8 9" aria-hidden="true" style={{ flexShrink: 0 }}><path d="M1,0.8 L7,4.5 L1,8.2 Z" fill="currentColor"/></svg>;
}

export function PlayDot({ size = 18 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
            <circle cx="9" cy="9" r="9" fill="rgba(0, 0, 0, .55)"/>
            <path d="M7.2,5.6 L12.4,9 L7.2,12.4 Z" fill="#fff"/>
        </svg>
    );
}

/** The small dark frame of a part with a play mark, for the chip. */
function MiniFrame({ src }: { src: string }) {
    const { css } = useTheme();
    return (
        <span className={css({ position: "relative", display: "block", width: "36px", height: "20px", flexShrink: 0, borderRadius: "4px", overflow: "hidden", backgroundColor: "#333" })}>
            <img src={src} alt="" className={css({ width: "100%", height: "100%", objectFit: "cover", display: "block" })}/>
            <span className={css({ position: "absolute", inset: 0, display: "grid", placeItems: "center", backgroundColor: "rgba(0, 0, 0, .42)", color: "#fff" })}>
                <PlayMark/>
            </span>
        </span>
    );
}

/**
 * A film's part where its question comes up, small enough for a title line: a hairline chip with a frame of the part,
 * "Roles explained" and "1:34". It looks the same before and after watching. Nothing plays until it is clicked.
 */
export function FilmLink({ film, part = WHOLE, label, place }: {
    film: FilmId;
    part?: FilmPart;
    label: string;
    /** where it sits, for analytics */
    place: string;
}) {
    const { css, theme } = useTheme();
    const { strings, format } = useStrings();
    const f = useHelpFilm(film, part);
    const length = clock(f.length);
    return (
        <button type="button" onClick={() => f.play(place)} aria-label={format(strings.HelpFilms.PlayVideo, `${label} · ${length}`)}
                className={css({
                    ...theme.typography.LabelSmall, fontSize: "13px", lineHeight: "18px",
                    display: "inline-flex", alignItems: "center", gap: "8px", flexShrink: 0, whiteSpace: "nowrap",
                    justifySelf: "start", alignSelf: "flex-start",
                    height: "28px", boxSizing: "border-box", padding: "3px 11px 3px 3px", margin: 0, borderRadius: "8px", cursor: "pointer",
                    border: `1px solid ${theme.customColors.hairline}`, backgroundColor: theme.colors.primaryB, color: theme.colors.contentPrimary,
                    ":hover": { backgroundColor: theme.customColors.translucent },
                    ":focus-visible": { outline: `2px solid ${theme.colors.accent}`, outlineOffset: "2px" },
                })}>
            <MiniFrame src={filmThumbnailAt(f.film, f.language, f.frame, 40)}/>
            <span>{label}</span>
            <span className={css({ color: theme.colors.contentTertiary, fontWeight: 400, fontVariantNumeric: "tabular-nums" })}>{length}</span>
        </button>
    );
}

/** A frame of the part, its title and "Video · length", where there is room on a page or in a dialog. */
export function FilmRow({ film, part = WHOLE, place, label }: {
    film: FilmId;
    part?: FilmPart;
    place: string;
    /** the row's title; the part's chapter or the film's title without it */
    label?: string;
}) {
    const { css, theme } = useTheme();
    const { strings, format } = useStrings();
    const f = useHelpFilm(film, part);
    const title = label ?? (part.from > 0 || part.to !== undefined ? chapterTitle(f.film, part.from, f.language) : filmTitle(strings, film));
    const length = clock(f.length);
    return (
        <button type="button" onClick={() => f.play(place)} aria-label={format(strings.HelpFilms.PlayVideo, `${title} · ${length}`)} className={css({
            display: "grid", gridTemplateColumns: "64px minmax(0, 1fr)", gap: "10px", alignItems: "center", textAlign: "left",
            justifySelf: "start", alignSelf: "flex-start", maxWidth: "360px", padding: "6px 14px 6px 6px", margin: 0, borderRadius: "10px",
            border: `1px solid ${theme.customColors.hairline}`, backgroundColor: theme.colors.primaryB, color: theme.colors.contentPrimary, cursor: "pointer",
            ":hover": { backgroundColor: theme.customColors.translucent },
            ":focus-visible": { outline: `2px solid ${theme.colors.accent}`, outlineOffset: "2px" },
        })}>
            <span className={css({ position: "relative", width: "64px", height: "36px", borderRadius: "6px", overflow: "hidden", backgroundColor: theme.customColors.translucent })}>
                <img src={filmThumbnailAt(f.film, f.language, f.frame, 72)} alt="" className={css({ width: "100%", height: "100%", objectFit: "cover", display: "block" })}/>
                <span className={css({ position: "absolute", inset: 0, display: "grid", placeItems: "center" })}>
                    <PlayDot/>
                </span>
            </span>
            <span className={css({ minWidth: 0 })}>
                <span className={css({ display: "block", ...theme.typography.LabelSmall, fontSize: "13px", lineHeight: "18px" })}>{title}</span>
                <span className={css({ display: "block", ...theme.typography.ParagraphXSmall, color: theme.colors.contentTertiary })}>
                    {format(strings.Setup.VideoLength, length)}
                </span>
            </span>
        </button>
    );
}

/**
 * Only a frame of the part with a play button and its length, for a setup step: the step's own action stays the one
 * thing that reads first. The title is in the tooltip.
 */
export function FilmFrame({ film, part = WHOLE, place, label }: {
    film: FilmId;
    part?: FilmPart;
    place: string;
    label?: string;
}) {
    const { css, theme } = useTheme();
    const { strings, format } = useStrings();
    const f = useHelpFilm(film, part);
    const title = label ?? (part.from > 0 || part.to !== undefined ? chapterTitle(f.film, part.from, f.language) : filmTitle(strings, film));
    const length = clock(f.length);
    return (
        <button type="button" onClick={() => f.play(place)} title={`${title} · ${length}`} aria-label={format(strings.HelpFilms.PlayVideo, `${title} · ${length}`)}
                className={css({
                    position: "relative", display: "block", justifySelf: "start", alignSelf: "flex-start", width: "72px", height: "40px", padding: 0, margin: 0,
                    borderRadius: "6px", overflow: "hidden", cursor: "pointer", border: `1px solid ${theme.customColors.hairline}`, boxSizing: "border-box",
                    backgroundColor: theme.customColors.translucent,
                    ":hover": { borderColor: theme.colors.contentTertiary },
                    ":focus-visible": { outline: `2px solid ${theme.colors.accent}`, outlineOffset: "2px" },
                })}>
            <img src={filmThumbnailAt(f.film, f.language, f.frame, 80)} alt="" className={css({ width: "100%", height: "100%", objectFit: "cover", display: "block" })}/>
            <span className={css({ position: "absolute", inset: 0, display: "grid", placeItems: "center" })}><PlayDot/></span>
            <span className={css({
                position: "absolute", right: "3px", bottom: "3px", padding: "0 4px", borderRadius: "3px", backgroundColor: "rgba(0, 0, 0, .66)",
                color: "#fff", fontSize: "10px", lineHeight: "14px", fontWeight: 500, fontVariantNumeric: "tabular-nums",
            })}>{length}</span>
        </button>
    );
}

/** A film on the Help page: its poster, length and title, and "Watched" once seen. */
export function FilmCard({ film, place }: { film: FilmId; place: string }) {
    const { css, theme } = useTheme();
    const { strings } = useStrings();
    const f = useHelpFilm(film);
    const poster = f.film.cuts[f.language]?.poster ?? 0;
    return (
        <button type="button" onClick={() => f.play(place)} className={css({
            display: "grid", gap: "8px", alignContent: "start", textAlign: "left", minWidth: 0,
            padding: "6px", margin: "-6px", border: "none", borderRadius: "10px", background: "transparent", cursor: "pointer", color: theme.colors.contentPrimary,
            ":hover": { backgroundColor: theme.customColors.translucent },
            ":focus-visible": { outline: `2px solid ${theme.colors.accent}`, outlineOffset: "2px" },
        })}>
            <span className={css({
                position: "relative", display: "block", width: "100%", aspectRatio: "16 / 9", borderRadius: "8px", overflow: "hidden",
                backgroundColor: theme.customColors.translucent, border: `1px solid ${theme.customColors.hairline}`, boxSizing: "border-box",
            })}>
                <img src={filmThumbnailAt(f.film, f.language, poster, 200)} alt="" className={css({ width: "100%", height: "100%", objectFit: "cover", display: "block" })}/>
                <span className={css({ position: "absolute", inset: 0, display: "grid", placeItems: "center" })}><PlayDot/></span>
                <span className={css({
                    position: "absolute", right: "6px", bottom: "6px", padding: "0 6px", borderRadius: "4px", backgroundColor: "rgba(0, 0, 0, .66)",
                    color: "#fff", fontSize: "11px", lineHeight: "16px", fontWeight: 500, fontVariantNumeric: "tabular-nums",
                })}>{clock(f.length)}</span>
            </span>
            <span className={css({ ...theme.typography.LabelSmall, lineHeight: "18px" })}>{filmTitle(strings, film)}</span>
            {f.watchedAny && (
                <span className={css({ display: "flex", alignItems: "center", gap: "5px", ...theme.typography.ParagraphXSmall, color: theme.colors.contentTertiary })}>
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.3l2.3 2.2 4.7-5" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>
                    {strings.HelpFilms.Watched}
                </span>
            )}
        </button>
    );
}
